/** Small WebGL2 helpers: program compilation, array textures, frustum culling. */

export function createProgram(gl: WebGL2RenderingContext, vsSource: string, fsSource: string, name: string): WebGLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSource, `${name}.vert`);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSource, `${name}.frag`);
  const program = gl.createProgram();
  if (!program) throw new Error('failed to create program');
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    throw new Error(`link error in ${name}: ${log}`);
  }
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  return program;
}

function compileShader(gl: WebGL2RenderingContext, type: number, source: string, name: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('failed to create shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? '';
    const numbered = source.split('\n').map((l, i) => `${String(i + 1).padStart(3)}: ${l}`).join('\n');
    throw new Error(`compile error in ${name}: ${log}\n${numbered}`);
  }
  return shader;
}

export function uniforms(gl: WebGL2RenderingContext, program: WebGLProgram, names: string[]): Record<string, WebGLUniformLocation | null> {
  const out: Record<string, WebGLUniformLocation | null> = {};
  for (const n of names) out[n] = gl.getUniformLocation(program, n);
  return out;
}

/**
 * Upload a vertical strip PNG as a 2D array texture, one layer per tile.
 *
 * Mipmaps are generated but the magnification filter stays NEAREST so the art
 * keeps its hard pixel edges; minification uses NEAREST_MIPMAP_LINEAR so
 * distant terrain does not shimmer.
 */
export async function loadArrayTexture(
  gl: WebGL2RenderingContext,
  url: string,
  tileSize: number,
): Promise<{ texture: WebGLTexture; layers: number }> {
  const image = await loadImage(url);
  if (image.width !== tileSize) {
    throw new Error(`${url}: expected width ${tileSize}, got ${image.width}`);
  }
  const layers = image.height / tileSize;
  if (!Number.isInteger(layers)) throw new Error(`${url}: height is not a multiple of ${tileSize}`);

  // The source is a vertical strip, which is already the memory layout a 2D
  // array texture expects, so it can be uploaded in one call.
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(image, 0, 0);
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);

  const texture = gl.createTexture();
  if (!texture) throw new Error('failed to create texture');
  gl.bindTexture(gl.TEXTURE_2D_ARRAY, texture);
  const levels = Math.floor(Math.log2(tileSize)) + 1;
  gl.texStorage3D(gl.TEXTURE_2D_ARRAY, levels, gl.RGBA8, tileSize, tileSize, layers);
  gl.texSubImage3D(
    gl.TEXTURE_2D_ARRAY, 0, 0, 0, 0, tileSize, tileSize, layers,
    gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(pixels.data.buffer),
  );
  gl.generateMipmap(gl.TEXTURE_2D_ARRAY);
  gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_LINEAR);
  gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.REPEAT);

  const aniso = gl.getExtension('EXT_texture_filter_anisotropic');
  if (aniso) {
    const max = gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number;
    gl.texParameterf(gl.TEXTURE_2D_ARRAY, aniso.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(4, max));
  }
  return { texture, layers };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`failed to load ${url}`));
    img.src = url;
  });
}

// ---------------------------------------------------------------------------
// Matrices (column-major, matching GLSL)
// ---------------------------------------------------------------------------

export type Mat4 = Float32Array;

export function mat4(): Mat4 {
  const m = new Float32Array(16);
  m[0] = 1;
  m[5] = 1;
  m[10] = 1;
  m[15] = 1;
  return m;
}

export function perspective(out: Mat4, fovY: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovY / 2);
  out.fill(0);
  out[0] = f / aspect;
  out[5] = f;
  out[10] = (far + near) / (near - far);
  out[11] = -1;
  out[14] = (2 * far * near) / (near - far);
  return out;
}

/**
 * View matrix from a yaw/pitch camera.
 *
 * Yaw 0 faces +Z (south) and increases anticlockwise, matching the server's
 * `yawToDirection`; positive pitch looks upward. The basis is built explicitly
 * and then transposed into the view transform.
 */
export function lookRotation(out: Mat4, x: number, y: number, z: number, yaw: number, pitch: number): Mat4 {
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);

  const fx = -sy * cp;
  const fy = sp;
  const fz = cy * cp;
  // right = normalize(cross(forward, worldUp)) = (-fz, 0, fx) / cos(pitch)
  const rx = -cy;
  const ry = 0;
  const rz = -sy;
  // up = cross(right, forward)
  const ux = ry * fz - rz * fy;
  const uy = rz * fx - rx * fz;
  const uz = rx * fy - ry * fx;

  out[0] = rx;
  out[1] = ux;
  out[2] = -fx;
  out[3] = 0;
  out[4] = ry;
  out[5] = uy;
  out[6] = -fy;
  out[7] = 0;
  out[8] = rz;
  out[9] = uz;
  out[10] = -fz;
  out[11] = 0;
  out[12] = -(rx * x + ry * y + rz * z);
  out[13] = -(ux * x + uy * y + uz * z);
  out[14] = fx * x + fy * y + fz * z;
  out[15] = 1;
  return out;
}

export function multiply(out: Mat4, a: Mat4, b: Mat4): Mat4 {
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      out[c * 4 + r] =
        a[r]! * b[c * 4]! +
        a[4 + r]! * b[c * 4 + 1]! +
        a[8 + r]! * b[c * 4 + 2]! +
        a[12 + r]! * b[c * 4 + 3]!;
    }
  }
  return out;
}

export function invert(out: Mat4, m: Mat4): Mat4 {
  const a00 = m[0]!, a01 = m[1]!, a02 = m[2]!, a03 = m[3]!;
  const a10 = m[4]!, a11 = m[5]!, a12 = m[6]!, a13 = m[7]!;
  const a20 = m[8]!, a21 = m[9]!, a22 = m[10]!, a23 = m[11]!;
  const a30 = m[12]!, a31 = m[13]!, a32 = m[14]!, a33 = m[15]!;

  const b00 = a00 * a11 - a01 * a10;
  const b01 = a00 * a12 - a02 * a10;
  const b02 = a00 * a13 - a03 * a10;
  const b03 = a01 * a12 - a02 * a11;
  const b04 = a01 * a13 - a03 * a11;
  const b05 = a02 * a13 - a03 * a12;
  const b06 = a20 * a31 - a21 * a30;
  const b07 = a20 * a32 - a22 * a30;
  const b08 = a20 * a33 - a23 * a30;
  const b09 = a21 * a32 - a22 * a31;
  const b10 = a21 * a33 - a23 * a31;
  const b11 = a22 * a33 - a23 * a32;

  let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (!det) return out;
  det = 1 / det;

  out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det;
  out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * det;
  out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det;
  out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * det;
  out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det;
  out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det;
  out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det;
  out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det;
  out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det;
  out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det;
  out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det;
  out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det;
  out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det;
  out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det;
  out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det;
  out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det;
  return out;
}

export function translationScale(out: Mat4, x: number, y: number, z: number, sx: number, sy: number, sz: number, yaw = 0): Mat4 {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  out[0] = c * sx;
  out[1] = 0;
  out[2] = -s * sx;
  out[3] = 0;
  out[4] = 0;
  out[5] = sy;
  out[6] = 0;
  out[7] = 0;
  out[8] = s * sz;
  out[9] = 0;
  out[10] = c * sz;
  out[11] = 0;
  out[12] = x;
  out[13] = y;
  out[14] = z;
  out[15] = 1;
  return out;
}

// ---------------------------------------------------------------------------
// Frustum culling
// ---------------------------------------------------------------------------

/** Six frustum planes extracted from a view-projection matrix. */
export class Frustum {
  readonly planes = new Float32Array(24);

  update(m: Mat4): void {
    const p = this.planes;
    // left, right, bottom, top, near, far
    for (let i = 0; i < 3; i++) {
      p[i * 8] = m[3]! + m[i * 4 + 0 + 0]!;
    }
    // Written out explicitly: rows of the matrix combined with the w row.
    const set = (idx: number, a: number, b: number, c: number, d: number) => {
      const len = Math.hypot(a, b, c) || 1;
      p[idx * 4] = a / len;
      p[idx * 4 + 1] = b / len;
      p[idx * 4 + 2] = c / len;
      p[idx * 4 + 3] = d / len;
    };
    const m0 = m[0]!, m1 = m[1]!, m2 = m[2]!, m3 = m[3]!;
    const m4 = m[4]!, m5 = m[5]!, m6 = m[6]!, m7 = m[7]!;
    const m8 = m[8]!, m9 = m[9]!, m10 = m[10]!, m11 = m[11]!;
    const m12 = m[12]!, m13 = m[13]!, m14 = m[14]!, m15 = m[15]!;

    set(0, m3 + m0, m7 + m4, m11 + m8, m15 + m12); // left
    set(1, m3 - m0, m7 - m4, m11 - m8, m15 - m12); // right
    set(2, m3 + m1, m7 + m5, m11 + m9, m15 + m13); // bottom
    set(3, m3 - m1, m7 - m5, m11 - m9, m15 - m13); // top
    set(4, m3 + m2, m7 + m6, m11 + m10, m15 + m14); // near
    set(5, m3 - m2, m7 - m6, m11 - m10, m15 - m14); // far
  }

  /** Conservative AABB test: false only when the box is fully outside. */
  intersectsBox(
    minX: number, minY: number, minZ: number,
    maxX: number, maxY: number, maxZ: number,
  ): boolean {
    const p = this.planes;
    for (let i = 0; i < 6; i++) {
      const a = p[i * 4]!;
      const b = p[i * 4 + 1]!;
      const c = p[i * 4 + 2]!;
      const d = p[i * 4 + 3]!;
      // Test the box corner furthest along the plane normal.
      const x = a > 0 ? maxX : minX;
      const y = b > 0 ? maxY : minY;
      const z = c > 0 ? maxZ : minZ;
      if (a * x + b * y + c * z + d < 0) return false;
    }
    return true;
  }
}
